import type { FeatureKey } from '../vocabulary';
import type { IncapacityKind } from './Incapacity';
import type { PermissionKind } from './PermissionRequest';

/**
 * Lo que un área cambia sobre los catálogos de incapacidades y permisos.
 *
 * Hasta aquí los dos catálogos eran de la empresa entera: una sola lista en
 * `config/catalogs` que editaba el superusuario, y las ocho áreas ofrecían
 * exactamente lo mismo. No es lo que pasa en la realidad — Customer Kare no
 * reparte home office como Tecnología, y quien sabe eso es quien dirige el área,
 * no quien administra la plataforma.
 *
 * **Guarda diferencias, no copias.** Un área que no ha tocado nada no tiene
 * documento, y hereda el catálogo global; el día que apaga un tipo se guarda esa
 * línea y nada más. Copiar la lista entera al crear el área parecía más simple y
 * es la forma de que un cambio global —añadir un tipo nuevo, corregir un texto—
 * deje de llegar a nadie, en silencio y sin que ninguna pantalla lo delate.
 *
 * Es el mismo trato que ya tienen las plantillas de correo en
 * `emailTemplateOverrides`, y por el mismo motivo.
 */
/**
 * Los módulos que un área puede encender o apagar por su cuenta.
 *
 * Sólo estos dos, y no los once de `FEATURES`: los demás —la validación del
 * IMSS, el kanban, el doble visto bueno— describen **cómo** trabaja la empresa
 * con un módulo, y eso no se reparte por área. Éstos dicen **si** el área tiene
 * el módulo, que es la pregunta que trajo esto.
 *
 * `imss` queda fuera a propósito aunque cuelgue de `incapacidades`: un área que
 * encienda incapacidades hereda la validación del IMSS tal como esté en la
 * empresa, porque el trato con el instituto es de la empresa y no del área.
 *
 * **`reemplazo` es la excepción, y entró a pedido el 26 oct 2026**: no dice si el
 * área tiene un módulo sino si exige nombrar quién cubre al pedir vacaciones, y
 * eso sí lo quiso decidir cada área. Lo respetan el asistente —por `featureOn`,
 * que ya pasa por aquí— y el servidor, con `featureLiveForArea`.
 */
export const AREA_FEATURES = ['permisos', 'incapacidades', 'reemplazo'] as const;
export type AreaFeatureKey = (typeof AREA_FEATURES)[number];

export const isAreaFeatureKey = (value: unknown): value is AreaFeatureKey =>
  typeof value === 'string' && (AREA_FEATURES as readonly string[]).includes(value);

/**
 * Lo que un área decide sobre un módulo.
 *
 * Tres estados y no dos, porque «apagado» y «no he tocado nada» **no son lo
 * mismo**: lo segundo tiene que seguir a la empresa cuando la empresa cambie, y
 * un booleano no sabe decir eso. Es la misma razón por la que los tipos de
 * catálogo guardan diferencias en vez de copias.
 */
export type AreaFeatureState = 'hereda' | 'encendido' | 'apagado';

export interface AreaCatalog {
  readonly areaId: string;
  /**
   * Entradas por id. Si el id existe en el catálogo global es una **corrección**
   * —se mezcla campo a campo sobre él—; si no existe, es un tipo **propio del
   * área**, que sólo se ofrece ahí.
   */
  readonly incapacityKinds: readonly Partial<IncapacityKind>[];
  readonly permissionKinds: readonly Partial<PermissionKind>[];
  /**
   * Los módulos que esta área enciende o apaga contra lo que diga la empresa.
   * Una llave ausente es «hereda», que es el estado de casi todas.
   */
  readonly features: Readonly<Partial<Record<AreaFeatureKey, boolean>>>;
}

/** Un catálogo de área vacío: hereda todo. */
export const emptyAreaCatalog = (areaId: string): AreaCatalog => ({
  areaId,
  incapacityKinds: [],
  permissionKinds: [],
  features: {},
});

/**
 * El catálogo que ve un área: el global, corregido y ampliado por ella.
 *
 * El orden importa y es el que se lee en pantalla: primero los de la empresa, en
 * el orden que tengan allí, y detrás los que el área inventó. Reordenar los
 * comunes porque un área tocó uno haría que la lista bailara según qué se hubiera
 * editado, que es justo lo que nadie espera de un catálogo.
 *
 * Una entrada del área **sin `id`** se descarta en vez de romper: el documento lo
 * escribe una callable que sí valida, pero esta función también la usan el
 * servidor al recibir una solicitud y la pantalla al pintar, y ninguna de las dos
 * puede permitirse caerse por un documento torcido.
 */
export const resolveKinds = <T extends { readonly id: string }>(
  global: readonly T[],
  overrides: readonly Partial<T>[],
): readonly T[] => {
  const conId = overrides.filter(
    (entry): entry is Partial<T> & { id: string } =>
      typeof entry.id === 'string' && entry.id.length > 0,
  );

  const porId = new Map(conId.map((entry) => [entry.id, entry]));
  const comunes = global.map((entry) => {
    const propio = porId.get(entry.id);
    return propio === undefined ? entry : { ...entry, ...propio };
  });

  const idsGlobales = new Set(global.map((entry) => entry.id));
  /*
   * Los propios del área conservan el orden en que los dio de alta y no se
   * ordenan por nombre: la lista de tipos es corta y quien la administra la
   * reconoce por su forma, no alfabéticamente.
   */
  const propios = conId.filter((entry) => !idsGlobales.has(entry.id)) as unknown as readonly T[];

  return [...comunes, ...propios];
};

/** Cuántas diferencias tiene un área respecto a la empresa. Cero es «hereda todo». */
export const overrideCount = (catalog: AreaCatalog): number =>
  catalog.incapacityKinds.length +
  catalog.permissionKinds.length +
  Object.keys(catalog.features).length;

/** En qué estado está un módulo para un área, sin mirar todavía a la empresa. */
export const areaFeatureState = (
  catalog: AreaCatalog | undefined,
  feature: AreaFeatureKey,
): AreaFeatureState => {
  const propio = catalog?.features[feature];
  if (propio === undefined) return 'hereda';
  return propio ? 'encendido' : 'apagado';
};

/**
 * Si un módulo está vivo para un área: lo que dice la empresa, corregido por lo
 * que diga el área.
 *
 * **Un área puede encender lo que la empresa tiene apagado**, que es justo para
 * lo que existe esto: un módulo global apagado no significa «prohibido» sino
 * «todavía no, en general». Y puede apagar lo que la empresa tiene encendido,
 * por simetría — el estado intermedio, «hereda», es el que deja que un cambio de
 * la empresa siga llegando.
 *
 * Los módulos que no se reparten por área —`imss`, `kanban`, los demás— caen a
 * lo global sin más, y `imss` además necesita que **en esa área** haya
 * incapacidades: ofrecer el botón de validación dentro de un módulo que el área
 * apagó sería pintar un control hacia una pantalla inalcanzable.
 */
export const featureLiveInArea = (
  global: Readonly<Record<FeatureKey, boolean>>,
  catalog: AreaCatalog | undefined,
  feature: FeatureKey,
): boolean => {
  const propio = isAreaFeatureKey(feature) ? catalog?.features[feature] : undefined;
  const activo = propio ?? global[feature] === true;
  if (!activo) return false;
  if (feature === 'imss') return featureLiveInArea(global, catalog, 'incapacidades');
  return true;
};
